import { useState, useEffect, DependencyList, useRef, useCallback } from "react";
import { AnyForNow } from "../types";
import { externalgetApi } from "../../../services/externalApis";
import { SETTING_API_15, GRN_API_12 } from "../../../api/ApiModel";
import { CON_BUSINESS_TYPECODE, CON_STATUS_ACTIVE } from "../../../utils/sys/Constants";

export const createInitialFilters = (includeReturnId = false) => ({
  grnId: "",
  ponumber: "",
  supplierCode: "",
  supplierName: "",
  warehouse: "",
  status: "",
  fromDate: null,
  toDate: null,
  searchValue: "",
  ...(includeReturnId ? { grnreturncode: "" } : {}),
});

export const useWarehouseOptions = (COMPCODE?: string) => {
  const [warehouseOptions, setWarehouseOptions] = useState<AnyForNow[]>([]);
  const hasFetchedRef = useRef(false);
  const compCodeRef = useRef<string | undefined>(COMPCODE);

  useEffect(() => {
    // Only fetch if COMPCODE is provided and hasn't been fetched yet, or COMPCODE changed
    if (!COMPCODE) {
      return;
    }

    // Reset fetch flag if COMPCODE changed
    if (compCodeRef.current !== COMPCODE) {
      hasFetchedRef.current = false;
      compCodeRef.current = COMPCODE;
    }

    // Prevent duplicate calls
    if (hasFetchedRef.current) {
      return;
    }

    const fetchData = async () => {
      try {
        hasFetchedRef.current = true;
        const params = {
          COMPCODE,
          BUSINESSTYPECODE: CON_BUSINESS_TYPECODE,
          STS: CON_STATUS_ACTIVE,
        };
        const response = await externalgetApi(SETTING_API_15, "GET", params);
        setWarehouseOptions(response || []);
      } catch (error) {
        console.error("Error fetching GRN warehouses", error);
        hasFetchedRef.current = false; // Reset on error to allow retry
      }
    };

    fetchData();
  }, [COMPCODE]);

  return warehouseOptions;
};

export interface UseGrnStatusTypesConfig {
  filter?: (item: AnyForNow) => boolean;
  mapStatus?: (item: AnyForNow) => AnyForNow;
  deps?: DependencyList;
}

export const useGrnStatusTypes = ({
  filter,
  mapStatus,
  deps,
}: UseGrnStatusTypesConfig = {}) => {
  const [statusTypes, setStatusTypes] = useState<AnyForNow[]>([]);
  const [statusOptions, setStatusOptions] = useState<AnyForNow[]>([]);
  const rawDataRef = useRef<AnyForNow[]>([]);
  const hasFetchedRef = useRef(false);
  const filterRef = useRef(filter);
  const mapStatusRef = useRef(mapStatus);

  // Update refs when they change
  useEffect(() => {
    filterRef.current = filter;
    mapStatusRef.current = mapStatus;
  }, [filter, mapStatus]);

  // Process raw data with current filter and mapStatus
  const processData = useCallback((rawData: AnyForNow[]) => {
    const filtered = rawData
      .filter((item) => (filterRef.current ? filterRef.current(item) : true))
      .map((item) =>
        mapStatusRef.current
          ? mapStatusRef.current(item)
          : {
              ...item,
              grnstatus: item.trenuserremark,
              grnstatuscode: item.trsts,
              colorcode: item.colorcode || "#000000",
            }
      );

    setStatusTypes(filtered);
    setStatusOptions(
      filtered.map((item) => ({
        code: item.grnstatuscode || item.trsts,
        label: item.grnstatus || item.trenuserremark,
        color: item.colorcode || "#000000",
      }))
    );
  }, []);

  // Fetch data only once
  useEffect(() => {
    if (hasFetchedRef.current) {
      return;
    }

    const fetchData = async () => {
      try {
        hasFetchedRef.current = true;
        const result = await externalgetApi(GRN_API_12, "GET", {
          STS: CON_STATUS_ACTIVE,
        });

        if (Array.isArray(result)) {
          rawDataRef.current = result;
          processData(result);
        }
      } catch (error) {
        console.error("Error fetching GRN status types:", error);
        hasFetchedRef.current = false; // Reset on error to allow retry
      }
    };

    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Only run once on mount

  // Re-process data when filter or mapStatus changes (via deps)
  useEffect(() => {
    if (hasFetchedRef.current && rawDataRef.current.length > 0) {
      processData(rawDataRef.current);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps ?? [filter, mapStatus]);

  return { statusTypes, statusOptions };
};
